<template>
  <div class="car-display">
    <div class="row">
      <div class="col-sm-12">
        <template v-for="relocation in car.LotRelocations">
          <template v-if="new Date() <= new Date(relocation.endDate)">
            <p class="callout callout-info" v-if="relocation.permanent">
              <span class="fa fa-random fa-3x fa-pull-left fa-fw"></span>
              This car has a lot relocation scheduled. It will be moved from <strong>{{ relocation.startDate | date_format }} to {{ relocation.endDate | date_format }}.</strong><br/>
              The car will be available at <strong>{{ relocation.Lot.dropdownLabel }}</strong> once it has been moved.
            </p>
            <p class="callout callout-info" v-else>
              <span class="fa fa-clock fa-3x fa-pull-left fa-fw"></span>
              This car has a temporary lot relocation scheduled.<br/> Between
              <strong>{{ relocation.startDate | date_format }} and
              {{ relocation.endDate | date_format }}.</strong>, the car will
              be available at <strong>{{ relocation.Lot.dropdownLabel }}</strong>.
            </p>
          </template>
        </template>
        <div v-if="car.carRetired" class="alert alert-danger">
          <span class="fa fa-exclamation-triangle"></span> This car has been retired from the {{ car.FleetContract.name }} fleet.
        </div>
        <div v-if="car.carSold" class="alert alert-danger">
          <span class="fa fa-exclamation-triangle"></span> This car has been sold.
        </div>
        <div class="toolbar text-right">
          <router-link class="btn btn-primary" :to="'/fleet/vehicles/edit/' + car.uuid">
            <span class="fa fa-edit fa-fw"></span> Edit
          </router-link>
          <dropdown-input label="Car Commands"
            label-icon="fa-wifi"
            :actions="carCommands"
            :dropright="true"
            @action="executeAction($event, car)">
          </dropdown-input>
          <dropdown-input label="Actions"
            label-icon="fa-car"
            :actions="carActions(car)"
            :dropright="true"
            @action="executeAction($event, car)">
          </dropdown-input>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-3">
        <div class="card card-primary">
          <div class="card-header">
            Car Details
          </div>
          <div class="card-body">
            <car-display :car="car"></car-display>
          </div>
        </div>
      </div>
      <div class="col-sm-9">
        <nav-tab :tab-count="6">
          <!-- Latest Reservations -->
          <div title="Latest Reservations" slot="tab1" icon="fa-calendar-alt">
            <div class="card card-primary">
              <div class="card-header">
                Latest Reservations
              </div>
              <div class="card-body">
                <search-form
                  :results="carReservations"
                  :total-results="carReservationsTotal"
                  :options="{
                    permalink: 'getCarReservations',
                    params: { carUuid: $route.params.carId }
                  }"
                  :columns="carReservationsColumns"
                  :tableDisplay="reservationTableDisplay"
                  @toggleDisplay="reservationTableDisplay = !reservationTableDisplay"
                  :allowToggle="true">
                </search-form>
              </div>
            </div>
          </div>
          <!-- Car Availability -->
          <div title="Car Availability" slot="tab2" icon="fa-clock">
            <car-availability v-if="availability"
              :availability="availability"
              :car="loadedCar"
              :date="new Date().toISOString()"
              @previous="previousAvailability()"
              @next="nextAvailability()"
              @change="changeAvailability($event)">
            </car-availability>
          </div>
          <!-- Car Message Log -->
          <div title="Car Messages Log" slot="tab3" icon="fa-file-alt">
            <div class="card card-primary">
              <div class="card-header">
                Car Message Log
              </div>
              <div class="card-body">
                <search-form
                  :results="carMessages"
                  :total-results="carMessagesTotal"
                  :type="carMessagesType"
                  :options="{
                    permalink: 'getCarMessages',
                    params: { carUuid: $route.params.carId }
                  }">
                </search-form>
              </div>
            </div>
          </div>
          <!-- Car Notes -->
          <div title="Car Notes" slot="tab4" icon="fa-sticky-note">
            <div class="card card-primary">
              <div class="card-header">
                Car Notes
              </div>
              <div class="card-body">
                <search-form
                  ref="carNotesSearchForm"
                  :results="carNotes"
                  :total-results="carNotesTotal"
                  :options="{
                    permalink: 'getCarNotes',
                    params: { carUuid: $route.params.carId }
                  }">
                </search-form>
              </div>
            </div>
          </div>
          <!-- Damage Log -->
          <div title="Damage Log" slot="tab5" icon="fa-exclamation-triangle">
            <div class="card card-primary">
              <div class="card-header">
                Damage Log
              </div>
              <div class="card-body">
                <search-form
                  :results="carDamages"
                  :total-results="carDamagesTotal"
                  :options="{
                    permalink: 'getCarDamages',
                    params: { carUuid: $route.params.carId }
                  }">
                </search-form>
              </div>
            </div>
          </div>
          <!-- Stats -->
          <div title="Utilisation" slot="tab6" icon="fa-chart-line">
            <vue-form ref="form" :options="{ permalink: 'carUsage' }"
              @load="refresh()">
              <template slot="actions">
                <div class="col-sm-8 offset-sm-4">
                  <button type="button" class="btn btn-primary" @click="refresh()">
                    <span class="fa fa-fw fa-sync"></span> Refresh
                  </button>
                </div>
              </template>
            </vue-form>
            <div class="row mt-2">
              <div class="col-md-6">
                <chart v-if="this.usageChart"
                  :single-total="true"
                  :data="this.usageChart"
                  title="Usage"
                  ref="usage">
                </chart>
              </div>
              <div class="col-md-6">
                <chart v-if="this.revenueChart"
                  :single-total="true"
                  :data="this.revenueChart"
                  title="Revenue"
                  type="line"
                  ref="revenue">
                </chart>
              </div>
            </div>
          </div>
        </nav-tab>
      </div>
    </div>
  </div>
</template>

<script>
import CarAvailability from '@/components/admin/CarAvailability.vue'
import CarMessageDisplay from '@/components/displays/CarMessageDisplay.vue'
import TekboxMessageDisplay from '@/components/displays/TekboxMessageDisplay.vue'
import CarDamageDisplay from '@/components/displays/CarDamageDisplay.vue'
import CarDisplay from '@/components/displays/CarDisplay.vue'
import ReservationDisplay from '@/components/displays/ReservationDisplay.vue'
import AccountNoteDisplay from '@/components/displays/AccountNoteDisplay.vue'
import DropdownInput from '@/components/inputs/DropdownInput'
import SearchForm from '@/components/SearchForm'
import VueForm from '@/components/Form'
import Chart from '@/components/Chart'
import NavTab from '@/components/NavTab'
import { mapActions, mapGetters } from 'vuex'
import Api from '@/vuex/api'
import {
  LOAD_CAR,
  LOAD_CAR_GRAPHS,
  INSTANT_RESERVATION,
  UNLOCK_CAR,
  IGNITION_ON,
  IGNITION_OFF,
  CAPTURE_CARD,
  LOCK_CAR,
  IMMOBILIZE_VEHICLE,
  GPS_LOCATION,
  NEW_RESERVATION,
  NEW_MAINTENANCE,
  EDIT_CAR,
  MOVE_CAR,
  REPORT_DAMAGE,
  ADD_CAR_NOTE
} from '@/vuex/types'

export default {
  name: 'CarDetailsDisplay',
  components: {
    CarAvailability,
    CarDamageDisplay,
    CarDisplay,
    CarMessageDisplay,
    Chart,
    DropdownInput,
    ReservationDisplay,
    AccountNoteDisplay,
    NavTab,
    SearchForm,
    TekboxMessageDisplay,
    VueForm
  },
  computed: {
    car () {
      return {
        ...this.loadedCar
      }
    },
    usageChart () {
      if (!this.carGraphs) return false
      return [{
        color: '153, 102, 255',
        data: this.carGraphs.resCount,
        label: 'Reservations',
        labels: this.carGraphs.labels
      }]
    },
    revenueChart () {
      if (!this.carGraphs) return false
      return [{
        color: '255, 159, 64',
        data: this.carGraphs.moneyCount,
        label: '$',
        labels: this.carGraphs.labels
      }]
    },
    ...mapGetters([
      'loadedCar',
      'carCommands',
      'carActions',
      'carGraphs',
      'carMessages',
      'carMessagesTotal',
      'carReservations',
      'carReservationsTotal',
      'carNotes',
      'carNotesTotal',
      'carDamages',
      'carDamagesTotal',
      'carReservationsColumns',
      'carMessagesType'
    ])
  },
  data () {
    return {
      reservationTableDisplay: true,
      rawData: {},
      totalReservations: 0,
      totalRevenue: 0,
      availabilityDate: false,
      availability: []
    }
  },
  watch: {
    async $route () {
      this.$store.dispatch(LOAD_CAR, { carUuid: this.$route.params.carId })
      this.refresh()
      this.$refs.carNotesSearchForm.refresh()
    }
  },
  async mounted () {
    this.$store.dispatch(LOAD_CAR, { carUuid: this.$route.params.carId })
    let instance = new Api()
    this.availabilityDate = moment().format()
    this.availability = await instance.getReservationAvailability(moment(this.availabilityDate).format('x'), this.$route.params.carId)
    this.refresh()
  },
  methods: {
    async refresh () {
      let formData = this.$refs.form.serializeForm()
      if (formData.CarUuid) {
        await this.$store.dispatch(LOAD_CAR_GRAPHS, formData)
        this.$refs.usage.refresh()
        this.$refs.revenue.refresh()
      }
    },
    async previousAvailability () {
      let instance = new Api()
      this.availabilityDate = moment(this.availabilityDate).subtract(1, 'days').format()
      this.availability = await instance.getReservationAvailability(moment(this.availabilityDate).format('x'), this.$route.params.carId)
    },
    async nextAvailability () {
      let instance = new Api()
      this.availabilityDate = moment(this.availabilityDate).add(1, 'days').format()
      this.availability = await instance.getReservationAvailability(moment(this.availabilityDate).format('x'), this.$route.params.carId)
    },
    async changeAvailability (date) {
      let instance = new Api()
      this.availabilityDate = moment(date).format()
      this.availability = await instance.getReservationAvailability(moment(this.availabilityDate).format('x'), this.$route.params.carId)
    },
    async executeAction (action, payload) {
      let result = await this[action](payload)
      if (result && result.updated) this.updatedAccount = result.updated
    },
    ...mapActions([
      INSTANT_RESERVATION,
      UNLOCK_CAR,
      LOCK_CAR,
      IMMOBILIZE_VEHICLE,
      GPS_LOCATION,
      NEW_RESERVATION,
      NEW_MAINTENANCE,
      EDIT_CAR,
      MOVE_CAR,
      IGNITION_ON,
      IGNITION_OFF,
      CAPTURE_CARD,
      REPORT_DAMAGE,
      ADD_CAR_NOTE
    ])
  }
}
</script>



// WEBPACK FOOTER //
// src/components/displays/CarDetailsDisplay.vue